import {
  type BaseSyntheticEvent,
  type FocusEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  type Control,
  type SubmitErrorHandler,
  useForm,
} from "react-hook-form";

import { toastError, toastSuccess } from "@alpacahq/ui/old";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { invalidateOrderRelatedQueries } from "~/v2/api/invalidateOrderRelatedQueries";
import { queryKeys } from "~/v2/api/queryKeys";
import type { Account, Margin } from "~/v2/api/rest/account";
import type { Clock } from "~/v2/api/rest/data";
import {
  type CreateMultiLegOrderRequest,
  createMultiLegOrder,
} from "~/v2/api/rest/order";
import {
  MULTI_LEG_ORDER_TYPE_OPTIONS,
  type MultiLegOrderType,
  type MultiLegTimeInForce,
} from "~/v2/constants/multiLegOrder";
import type { PositionIntent } from "~/v2/constants/optionOrder";
import type { SelectedContract } from "~/v2/pages/dashboard/tradeV2";
import {
  amplitudeTradeTracking,
  type OrderEvent,
} from "~/v2/pages/dashboard/tradeV2/amplitudeTradeTracking";
import {
  type UnderlyingDisplay,
  useUnderlying,
} from "../option/hooks/useUnderlying";
import {
  type BuyingPowerResult,
  useBuyingPower,
} from "../shared/option/hooks/useBuyingPower";
import {
  type TimeInForceOption,
  useTimeInForceOptions,
} from "../shared/option/hooks/useTimeInForceOptions";
import { concatFormErrorMessages } from "../shared/utils";
import { type BidAskMidResult, useBidAskMid } from "./hooks/useBidAskMid";
import { useEstimatedCost } from "./hooks/useEstimatedCost";
import { type MultiLegFeesResult, useFees } from "./hooks/useFees";
import { useLimitPrice } from "./hooks/useLimitPrice";
import {
  type MultiLegOrderSchema,
  multiLegSchemaSuperRefinement,
  zMultiLegOrderSchema,
} from "./schema";
import {
  type CombinedLeg,
  combineLegs,
  getIsMultiLegSubmitDisabled,
} from "./utils";

const SUBMIT_COMPLETED_TIMEOUT = 500;

const DEFAULT_FORM_VALUES: Partial<MultiLegOrderSchema> = {
  orderType: "limit",
  timeInForce: "day",
  quantity: 1,
  legs: [],
};

interface UseMultiLegOrderTicketControllerProps {
  account: Account | undefined;
  margin: Margin | undefined;
  product: string | undefined;
  clock: Clock | undefined;
  underlyingSymbol: string;
  propLegs: readonly SelectedContract[];
  onRemoveLeg: (symbol: string) => void;
  onUpdateLegType: (symbol: string, type: string) => void;
}

export const useMultiLegOrderTicketController = ({
  account,
  margin,
  product,
  clock,
  underlyingSymbol,
  propLegs,
  onRemoveLeg,
  onUpdateLegType,
}: UseMultiLegOrderTicketControllerProps): UseMultiLegControllerReturn => {
  const [isSubmitCompleted, setIsSubmitCompleted] = useState(false);

  const { underlying } = useUnderlying({
    underlyingSymbol,
  });

  const {
    setValue,
    watch,
    control,
    handleSubmit,
    reset: resetForm,
  } = useForm<MultiLegOrderSchema>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: "onChange",
    resolver: zodResolver(
      zMultiLegOrderSchema.superRefine(multiLegSchemaSuperRefinement),
    ),
  });

  const orderType = watch("orderType");
  const limitPrice = watch("limitPrice");
  const quantity = watch("quantity");
  const timeInForce = watch("timeInForce");
  const formLegs = watch("legs");

  const legs = useMemo(
    () => combineLegs(propLegs, formLegs),
    [propLegs, formLegs],
  );

  const formLegsRef = useRef(formLegs);
  formLegsRef.current = formLegs;

  const bidAskMid = useBidAskMid(legs);
  const buyingPower = useBuyingPower({ margin });
  const { estimatedCost, isCredit } = useEstimatedCost({
    legs,
    quantity,
    orderType,
    limitPrice,
  });
  const fees = useFees(legs, quantity);
  const timeInForceOptions = useTimeInForceOptions(orderType, clock);

  useLimitPrice({
    legs,
    orderType,
    setLimitPrice: (value) => setValue("limitPrice", value),
  });

  // Sync form legs when prop legs change
  useEffect(() => {
    const currentFormLegs = formLegsRef.current;
    const updatedFormLegs: MultiLegOrderSchema["legs"] = propLegs.map(
      ({ contract, quoteSide }) => {
        const formLeg = currentFormLegs.find(
          ({ symbol }) => symbol === contract.symbol,
        );

        const positionIntent: PositionIntent =
          (formLeg?.positionIntent.includes("sell") && quoteSide === "bid") ||
          (formLeg?.positionIntent.includes("buy") && quoteSide === "ask")
            ? formLeg.positionIntent
            : quoteSide === "ask"
              ? "buy_to_open"
              : "sell_to_open";

        const ratioQty = formLeg?.ratioQty ?? 1;

        return {
          symbol: contract.symbol,
          positionIntent,
          ratioQty,
        };
      },
    );

    setValue("legs", updatedFormLegs);
  }, [propLegs, setValue]);

  const orderEventData: OrderEvent = {
    order_type: orderType,
    asset_symbol: underlyingSymbol,
    asset_class: "us_option",
    qty: quantity,
    time_in_force: timeInForce,
  };

  const queryClient = useQueryClient();

  const onRefreshQuote = () => {
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.options.all, "chain-snapshots"],
    });
  };

  const invalidateOrderAndPositionQueries = () => {
    invalidateOrderRelatedQueries(queryClient);
  };

  const mutation = useMutationRequest(
    (data: MultiLegOrderSchema) => {
      const orderRequest: CreateMultiLegOrderRequest = {
        type: data.orderType,
        time_in_force: data.timeInForce,
        qty: data.quantity.toString(),
        legs: data.legs.map(({ symbol, positionIntent, ratioQty }) => ({
          symbol,
          side: positionIntent.includes("sell")
            ? ("sell" as const)
            : ("buy" as const),
          position_intent: positionIntent,
          ratio_qty: ratioQty.toString(),
        })),
      };

      if (data.orderType === "limit" && data.limitPrice != null) {
        orderRequest.limit_price = data.limitPrice.toString();
      }

      return createMultiLegOrder(
        account?.id ?? "",
        orderRequest,
        product as "paper" | "live",
      );
    },
    {
      onSuccess: () => {
        toastSuccess("Your order has been placed successfully");
        invalidateOrderAndPositionQueries();
        handleCancelOrder();
        setIsSubmitCompleted(true);
        setTimeout(() => {
          setIsSubmitCompleted(false);
          resetForm(DEFAULT_FORM_VALUES);
        }, SUBMIT_COMPLETED_TIMEOUT);
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toastError(`Order failed: ${errorMessage}`);
      },
    },
  );

  const onValidSubmit = (data: MultiLegOrderSchema) => {
    if (!mutation.isPending) {
      mutation.mutate(data);
      amplitudeTradeTracking.confirmOrderClicked(orderEventData);
    }
  };

  const onInvalidSubmit: SubmitErrorHandler<MultiLegOrderSchema> = (errors) =>
    toastError(`Invalid Order: ${concatFormErrorMessages(errors)}`);

  const onSubmit = handleSubmit(onValidSubmit, onInvalidSubmit);

  const handleCancelOrder = () =>
    propLegs.forEach(({ contract: { symbol } }) => {
      onRemoveLeg(symbol);
    });

  const onCancel = () => {
    handleCancelOrder();
    resetForm(DEFAULT_FORM_VALUES);
  };

  const onOrderTypeChange = (newOrderType: MultiLegOrderType) => {
    setValue("orderType", newOrderType);
    amplitudeTradeTracking.orderTypeChanged(newOrderType);
  };

  const onTimeInForceChange = (newTimeInForce: MultiLegTimeInForce) => {
    setValue("timeInForce", newTimeInForce);
  };

  const onLegPositionIntentChange = (
    symbol: string,
    positionIntent: PositionIntent,
  ) => {
    setValue(
      "legs",
      formLegs.map((leg) =>
        leg.symbol === symbol ? { ...leg, positionIntent } : leg,
      ),
    );
  };

  const onLegRatioQtyChange = (symbol: string, ratioQty: number) => {
    setValue(
      "legs",
      formLegs.map((leg) =>
        leg.symbol === symbol ? { ...leg, ratioQty } : leg,
      ),
    );
  };

  const onQuantityBlur: FocusEventHandler<HTMLInputElement> = (event) =>
    amplitudeTradeTracking.quantityChanged(event.target.value);

  const isSubmitDisabled = useMemo(
    () =>
      getIsMultiLegSubmitDisabled({
        isSubmitCompleted,
        account,
        product,
        formLegs,
        quantity,
        orderType,
        limitPrice,
      }),
    [
      isSubmitCompleted,
      account,
      product,
      formLegs,
      quantity,
      orderType,
      limitPrice,
    ],
  );

  return {
    control,
    onSubmit,
    onCancel,
    onQuantityBlur,
    isSubmitDisabled,
    isSubmitLoading: mutation.isPending,
    isSubmitCompleted,

    underlying,

    legs,
    onLegPositionIntentChange,
    onLegRatioQtyChange,
    onUpdateLegType,
    onRemoveLeg,

    bidAskMid,
    isCredit,

    orderType,
    onOrderTypeChange,
    orderTypeOptions: MULTI_LEG_ORDER_TYPE_OPTIONS,

    timeInForce,
    onTimeInForceChange,
    timeInForceOptions,

    estimatedCost,
    buyingPower,
    fees,
    onRefreshQuote,
  };
};

export type UseMultiLegControllerReturn = {
  control: Control<MultiLegOrderSchema>;
  onSubmit: (event?: BaseSyntheticEvent) => Promise<void>;
  onCancel: () => void;
  onQuantityBlur: FocusEventHandler<HTMLInputElement>;
  isSubmitDisabled: boolean;
  isSubmitLoading: boolean;
  isSubmitCompleted: boolean;

  underlying: UnderlyingDisplay;

  legs: CombinedLeg[];
  onLegPositionIntentChange: (symbol: string, intent: PositionIntent) => void;
  onLegRatioQtyChange: (symbol: string, ratioQty: number) => void;
  onUpdateLegType: (symbol: string, type: string) => void;
  onRemoveLeg: (symbol: string) => void;

  bidAskMid: BidAskMidResult;
  isCredit: boolean;

  orderType: MultiLegOrderType;
  onOrderTypeChange: (orderType: MultiLegOrderType) => void;
  orderTypeOptions: { value: MultiLegOrderType; label: string }[];

  timeInForce: MultiLegTimeInForce;
  onTimeInForceChange: (timeInForce: MultiLegTimeInForce) => void;
  timeInForceOptions: TimeInForceOption[];

  estimatedCost: string;
  buyingPower: BuyingPowerResult;
  fees: MultiLegFeesResult;
  onRefreshQuote: () => void;
};
